/**
 * Custom Element: Variant Picker
 * Handles updating product info when options change.
 * Distinguishes between "Standard Variants" (Inputs) and "Combined Listings" (Links).
 */

/**
 * Build a Section Rendering API URL for a product page (combined listing child swatches).
 */
function buildProductSectionRenderUrl(productUrl, sectionId) {
  const u = new URL(productUrl, window.location.origin);

  u.searchParams.delete('variant');
  u.searchParams.set('section_id', sectionId);

  const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');

  if (previewThemeId) {
    u.searchParams.set('preview_theme_id', previewThemeId);
  }

  return u.toString();
}

/**
 * Section Rendering API: refresh the Product description section when the PDP URL
 * changes (combined listing child swatches).
 */
function resolveProductDescriptionSectionRenderingId() {
  const el = document.getElementById('product-description');

  if (el?.dataset?.sectionRenderingId) {
    return el.dataset.sectionRenderingId;
  }

  const wrap = document.querySelector('.product-description')?.closest('[id^="shopify-section-"]');

  if (wrap?.id) {
    return wrap.id.replace(/^shopify-section-/, '');
  }

  return 'product_description';
}

function refreshProductDescriptionSection(productUrl) {
  const sectionId = resolveProductDescriptionSectionRenderingId();

  if (!sectionId || typeof fetchHTML !== 'function') {
    return Promise.resolve();
  }

  let fetchUrl;

  try {
    fetchUrl = buildProductSectionRenderUrl(productUrl, sectionId);
  } catch {
    return Promise.resolve();
  }

  return fetchHTML(fetchUrl)
    .then((doc) => {
      const wrapId = `shopify-section-${sectionId}`;
      const incoming = doc.getElementById(wrapId);
      const current = document.getElementById(wrapId);

      if (incoming && current) {
        current.replaceWith(document.importNode(incoming, true));
      } else if (incoming && !current) {
        const picker = document.querySelector('variant-picker');
        const mainId = picker?.dataset?.section;

        if (mainId) {
          const mainEl = document.getElementById(`shopify-section-${mainId}`);

          if (mainEl) {
            mainEl.insertAdjacentElement('afterend', document.importNode(incoming, true));
          }
        }
      } else if (!incoming && current) {
        current.remove();
      }

      document.dispatchEvent(new CustomEvent('product-description:updated'));
    })
    .catch((e) => {
      console.error(e);
    });
}

/**
 * Loox standard reviews widget (#looxReviews): resolve apps section id for Section Rendering API.
 */
function resolveLooxReviewsSectionRenderingId() {
  const looxReviews = document.getElementById('looxReviews');

  if (looxReviews?.dataset?.sectionRenderingId) {
    return looxReviews.dataset.sectionRenderingId;
  }

  const wrap = looxReviews?.closest('[id^="shopify-section-"]');

  if (wrap?.id) {
    return wrap.id.replace(/^shopify-section-/, '');
  }

  return null;
}

function parseLooxProductIdFromDocument(doc) {
  return doc.getElementById('looxReviews')?.getAttribute('data-product-id') || null;
}

/**
 * Re-init Loox standard widget after combined listing colour change.
 * Loox polls #looxReviews every 1s and only injects looxReviewsFrame when data-upgraded is absent.
 */
function reinitLooxReviewsWidget(productId) {
  const container = document.getElementById('looxReviews');

  if (!container || !productId) {
    return;
  }

  container.setAttribute('data-product-id', productId);
  container.removeAttribute('data-upgraded');
  container.innerHTML = '';
  container.style.removeProperty('height');
  container.style.removeProperty('max-height');
  container.style.removeProperty('min-height');

  const loox = window.LOOX;

  if (loox) {
    loox.productId = productId;
  }
}

let looxReviewsPrimeWatcher = null;

function stopLooxReviewsPrimeWatcher() {
  looxReviewsPrimeWatcher?.stop();
  looxReviewsPrimeWatcher = null;
}

function clearCombinedListingParentLooxAttrs() {
  const productEl = document.querySelector('.product[data-combined-listing-parent]');

  if (!productEl) {
    return;
  }

  productEl.removeAttribute('data-combined-listing-parent');
  productEl.removeAttribute('data-loox-product-id');
}

function resolveCombinedListingLooxProductId() {
  const el = document.querySelector('.product[data-combined-listing-parent][data-loox-product-id]');

  return el?.dataset.looxProductId || null;
}

/**
 * Fallback when Loox's upgrade poll does not inject #looxReviewsFrame in time.
 */
function injectLooxReviewsFrame(productId) {
  const loox = window.LOOX;
  const container = document.getElementById('looxReviews');

  if (!loox?.root || !loox?.clientId || !loox?.hash || !container || !productId) {
    return;
  }

  const params = [`h=${loox.hash}`, `productIds=${productId}`];
  const reviewsToDisplay = container.getAttribute('data-reviews-to-display');

  if (reviewsToDisplay) {
    params.push(`reviews_to_display=${encodeURIComponent(reviewsToDisplay)}`);
  }

  const defaultTab = container.getAttribute('data-default-tab');

  if (defaultTab) {
    params.push(`default_tab=${encodeURIComponent(defaultTab)}`);
  }

  const src = `${loox.root}/widget/${loox.clientId}/reviews?${params.join('&')}`;
  let frame = container.querySelector('#looxReviewsFrame');

  if (!frame) {
    frame = document.createElement('iframe');
    frame.id = 'looxReviewsFrame';
    frame.title = 'Reviews widget';
    frame.height = '0';
    frame.width = '100%';
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('scrolling', 'no');
    frame.setAttribute('margin', '0');
    frame.setAttribute('style', 'overflow:hidden');
    container.appendChild(frame);
  }

  if (frame.getAttribute('src') !== src) {
    frame.setAttribute('src', src);
  }

  container.setAttribute('data-upgraded', 'true');
  loox.productId = productId;
}

/**
 * Combined listing parent: Loox app block uses parent product id (no reviews).
 * Use first linked child id until a colour swatch loads a child PDP via AJAX.
 */
function primeLooxReviewsForCombinedListingParent() {
  stopLooxReviewsPrimeWatcher();

  const targetId = resolveCombinedListingLooxProductId();

  if (!targetId) {
    return;
  }

  let frameFallbackScheduled = false;

  const scheduleFrameFallback = () => {
    if (frameFallbackScheduled) {
      return;
    }

    frameFallbackScheduled = true;

    window.setTimeout(() => {
      const container = document.getElementById('looxReviews');

      if (!container || !resolveCombinedListingLooxProductId()) {
        return;
      }

      if (!container.querySelector('iframe')) {
        injectLooxReviewsFrame(targetId);
      }
    }, 2000);
  };

  const apply = () => {
    const container = document.getElementById('looxReviews');

    if (!container) {
      return 'wait';
    }

    const currentId = container.getAttribute('data-product-id');
    const hasFrame = Boolean(container.querySelector('iframe'));

    if (currentId === targetId && hasFrame) {
      return 'done';
    }

    if (currentId !== targetId) {
      reinitLooxReviewsWidget(targetId);
      scheduleFrameFallback();
      return 'wait';
    }

    if (container.hasAttribute('data-upgraded') && !hasFrame) {
      reinitLooxReviewsWidget(targetId);
      scheduleFrameFallback();
      return 'wait';
    }

    scheduleFrameFallback();

    return 'wait';
  };

  let observer;
  let poll;

  const stopWatching = () => {
    observer?.disconnect();
    if (poll) {
      window.clearInterval(poll);
    }
    looxReviewsPrimeWatcher = null;
  };

  looxReviewsPrimeWatcher = { stop: stopWatching };

  const run = () => {
    const status = apply();

    if (status === 'done') {
      stopWatching();
    }
  };

  run();

  observer = new MutationObserver(() => {
    run();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  let attempts = 0;

  poll = window.setInterval(() => {
    attempts += 1;
    run();

    if (attempts >= 15) {
      stopWatching();
    }
  }, 1000);
}

function refreshLooxReviewsSection(productUrl) {
  if (!document.getElementById('looxReviews') || typeof fetchHTML !== 'function') {
    return Promise.resolve();
  }

  const sectionId = resolveLooxReviewsSectionRenderingId();

  if (!sectionId) {
    return Promise.resolve();
  }

  let fetchUrl;

  try {
    fetchUrl = buildProductSectionRenderUrl(productUrl, sectionId);
  } catch {
    return Promise.resolve();
  }

  return fetchHTML(fetchUrl)
    .then((doc) => {
      const productId = parseLooxProductIdFromDocument(doc);

      if (!productId) {
        return;
      }

      const currentLoox = document.getElementById('looxReviews');
      const incomingLoox = doc.getElementById('looxReviews');

      if (currentLoox && incomingLoox) {
        currentLoox.replaceWith(document.importNode(incomingLoox, true));
      }

      reinitLooxReviewsWidget(productId);

      window.setTimeout(() => {
        const container = document.getElementById('looxReviews');

        if (container && !container.querySelector('iframe')) {
          injectLooxReviewsFrame(productId);
        }
      }, 2000);
    })
    .catch((e) => {
      console.error(e);
    });
}

document.addEventListener('DOMContentLoaded', primeLooxReviewsForCombinedListingParent);

/**
 * Size guide dock-modal sits outside `.product__grid`, so combined listing
 * colour swaps must copy it from the already-fetched Product section HTML.
 * Re-inits the size chart; re-inits the quiz only if the modal is already open
 * (otherwise the existing trigger-click deferral still applies).
 */
function refreshSizeGuideFromDocument(html) {
  const destination = document.querySelector('.size-guide__content');
  const source = html.querySelector('.size-guide__content');

  if (!destination) {
    return;
  }

  if (!source) {
    destination.innerHTML = '';
    return;
  }

  destination.innerHTML = source.innerHTML;

  if (typeof window.initSizeChartElement === 'function') {
    for (const el of destination.querySelectorAll('.size-chart-content')) {
      window.initSizeChartElement(el);
    }
  }

  const modal = destination.closest('dock-modal');
  const modalIsOpen = Boolean(modal?.classList.contains('active'));

  if (!modalIsOpen || typeof window.initSizeQuizElement !== 'function') {
    return;
  }

  for (const el of destination.querySelectorAll('.js-size-quiz')) {
    window.initSizeQuizElement(el);
  }
}

/**
 * @param {Element | null | undefined} submit
 */
function syncProductContextTriggersFromSubmit(submit) {
  if (!submit) return;

  const span = submit.querySelector('span');
  const note = span?.querySelector('.product-form__pre-order-note');
  // On pre-order the note is a second line inside the span; triggers should only show the main label.
  const label = note
    ? (span.firstChild?.textContent?.trim() ?? '')
    : (span?.textContent?.trim() ?? submit.textContent?.trim() ?? '');

  if (!label) return;

  for (const trigger of document.querySelectorAll('.js-product-trigger')) {
    trigger.textContent = label;
  }
}

function syncProductContextTriggersFromMainForm() {
  const submit = document.querySelector('.product product-form .product-form__submit');

  syncProductContextTriggersFromSubmit(submit);
}

document.addEventListener('DOMContentLoaded', () => {
  requestAnimationFrame(syncProductContextTriggersFromMainForm);
});

/**
 * PDP product info rating badge → Loox reviews widget.
 */
document.addEventListener('click', (event) => {
  const link = event.target.closest('.product__rating-link');

  if (!link) {
    return;
  }

  const looxWidget = document.querySelector('.loox-widget');

  if (!looxWidget) {
    return;
  }

  event.preventDefault();
  looxWidget.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.variantData = null;
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.initVariantData();
    this.initPreOrderData();
    this.setupLabelUpdates();
    this.setupLinkListeners();
    this.#setupSizeNudge();

    // Initial check
    this.updateOptions();
    this.checkSelectionStatus();

    if (this.dataset.requiresExplicitVariant === 'true') {
      this.#initExplicitVariantMode();
    }

    const initialVariant = this.#getResolvedVariantFromInputs();
    this.#syncKlaviyoBisTrigger(initialVariant);
    if (initialVariant) {
      this.#syncSubmitButtonForVariant(initialVariant);
    } else {
      this.#syncPreOrderHint(null);
    }
  }

  initPreOrderData() {
    this.preOrderData = null;
    const script = this.closest('.product')?.querySelector('[data-product-pre-order]');

    if (!script) {
      return;
    }

    try {
      this.preOrderData = JSON.parse(script.textContent);
    } catch (e) {
      console.error('Failed to parse pre-order data', e);
    }
  }

  #getPreOrderTextForVariant(variant) {
    if (!variant || !this.preOrderData) {
      return '';
    }

    const vid = String(variant.id);
    const variantText = this.preOrderData.variants?.[vid];

    if (variantText) {
      return variantText;
    }

    return this.preOrderData.productText || '';
  }

  #isEffectivelyOutOfStock(variant) {
    if (!variant) {
      return false;
    }

    const mappedOutOfStock = this.preOrderData?.outOfStock?.[String(variant.id)];
    if (typeof mappedOutOfStock === 'boolean') {
      return mappedOutOfStock;
    }

    if (variant.available === false) {
      return true;
    }

    return variant.inventory_policy === 'continue' && Number(variant.inventory_quantity ?? 0) <= 0;
  }

  #isPreOrderVariant(variant) {
    return this.#isEffectivelyOutOfStock(variant) && this.#getPreOrderTextForVariant(variant) !== '';
  }

  #syncPreOrderHint(variant) {
    const sectionRoot =
      this.closest('.section-product') || this.closest('.shopify-section') || document.documentElement;
    const show = this.#isPreOrderVariant(variant);

    for (const el of sectionRoot.querySelectorAll('.js-pre-order-hint')) {
      el.classList.toggle('hidden', !show);
    }
  }

  #setPreOrderProperty(form, text) {
    if (!form) {
      return;
    }

    const name = 'properties[Pre Order]';
    let input = form.querySelector(`[name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      const buttons = form.querySelector('.product-form__buttons');

      if (buttons) {
        form.insertBefore(input, buttons);
      } else {
        form.appendChild(input);
      }
    }

    input.value = text;
  }

  #clearPreOrderProperty(form) {
    form?.querySelector('[name="properties[Pre Order]"]')?.remove();
  }

  #setupSizeNudge() {
    const nudge = this.querySelector('.js-size-nudge');
    if (!nudge) return;

    // Reveal on next frame so the CSS transition runs
    requestAnimationFrame(() => nudge.classList.add('size-nudge--visible'));

    const fieldset = nudge.closest('fieldset');
    if (!fieldset) return;

    const hide = () => {
      nudge.classList.remove('size-nudge--visible');
      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        input.removeEventListener('change', hide);
      }
    };

    for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
      input.addEventListener('change', hide);
    }
  }

  #initExplicitVariantMode() {
    const params = new URLSearchParams(window.location.search);
    const vid = params.get('variant');

    if (vid && this.variantData) {
      const variant = this.variantData.find((v) => String(v.id) === String(vid));
      if (variant) {
        this.#setInputsForVariant(variant);
        this.updateOptions();
        const trigger = this.querySelector('fieldset.product-form__input input:checked');
        if (trigger) {
          trigger.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }

    this.#applyIncompleteVariantUI();
  }

  #setInputsForVariant(variant) {
    const fieldsets = Array.from(this.querySelectorAll('fieldset.product-form__input'));
    let i = 0;

    for (const fieldset of fieldsets) {
      const val = variant.options[i];
      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((inp) => inp.value === val);

      if (input) {
        input.checked = true;
      }

      i++;
    }
  }

  #syncProductContextTriggers(submit) {
    syncProductContextTriggersFromSubmit(submit);
  }

  #applyIncompleteVariantUI() {
    const form = document.getElementById(`product-form-${this.sectionId}`);
    if (!form) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = '';
      idInput.disabled = true;
    }

    const submit = form.querySelector('.product-form__submit');
    if (submit) {
      submit.disabled = true;
      const span = submit.querySelector('span');
      const label = this.dataset.chooseOptionsLabel;

      if (span && label) {
        span.textContent = label;
      }

      this.#syncProductContextTriggers(submit);
    }

    const url = new URL(window.location);
    url.searchParams.delete('variant');
    window.history.replaceState({}, '', url.toString());

    this.#syncKlaviyoBisTrigger(null);
    this.#syncPreOrderHint(null);
    this.#clearPreOrderProperty(form);
    this.#clearProductAddOn();
  }

  /**
   * No resolved variant means no add-on can be matched; empty the wrapper until a
   * full selection triggers updateSectionParts, which swaps in the fresh card.
   */
  #clearProductAddOn() {
    const sectionRoot =
      this.closest('.section-product') || this.closest('.shopify-section') || document.documentElement;
    const addOn = sectionRoot.querySelector('[data-product-add-on]');

    if (addOn) {
      addOn.innerHTML = '';
      addOn.classList.remove('product-upsell');
    }
  }

  #getSelectedOptionsFromInputs() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset.product-form__input'));

    return fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }

  #getResolvedVariantFromInputs() {
    if (!this.variantData) return null;

    const selectedOptions = this.#getSelectedOptionsFromInputs();
    const allSelected = selectedOptions.every((v) => v !== null);

    if (!allSelected) return null;

    return (
      this.variantData.find((variant) => {
        return variant.options.every((opt, i) => opt === selectedOptions[i]);
      }) ?? null
    );
  }

  /**
   * Best-match variant for gallery sync — works with partial selection (e.g. colour before size).
   */
  #getGalleryVariantFromInputs() {
    if (!this.variantData) return null;

    const selectedOptions = this.#getSelectedOptionsFromInputs();

    if (!selectedOptions.some((value) => value !== null)) {
      return null;
    }

    return (
      this.variantData.find((variant) => {
        return selectedOptions.every((value, index) => value === null || variant.options[index] === value);
      }) ?? null
    );
  }

  #syncGalleryForCurrentSelection() {
    const variant = this.#getGalleryVariantFromInputs();

    if (!variant?.featured_media) {
      return;
    }

    const gallery = this.closest('.product__grid')?.querySelector('gallery-slider');

    if (gallery && typeof gallery.scrollToMediaId === 'function') {
      gallery.scrollToMediaId(variant.featured_media.id);
    }
  }

  #syncKlaviyoBisTrigger(variant) {
    const el = this.querySelector('.js-klaviyo-bis-trigger');
    if (!el) return;

    // Use theme `.hidden` (display: none !important): HTML `hidden` loses to `.button { display: inline-flex }`.
    // Klaviyo BIS does not bind for continue-selling variants; hide when pre-order is the purchase path.
    const show = this.#isEffectivelyOutOfStock(variant) && !this.#isPreOrderVariant(variant);
    el.classList.toggle('hidden', !show);
  }

  #findVariantForOptionValue(optionIndex, value) {
    if (!this.variantData) return null;

    const fieldsets = Array.from(this.querySelectorAll('fieldset.product-form__input'));

    return (
      this.variantData.find((variant) => {
        if (variant.options[optionIndex] !== value) return false;

        for (const fieldset of fieldsets) {
          const otherOptionIndex = Number(fieldset.dataset.optionIndex);

          if (otherOptionIndex === optionIndex) continue;

          const checked = fieldset.querySelector('input[type="radio"]:checked');

          if (checked && variant.options[otherOptionIndex] !== checked.value) return false;
        }

        return true;
      }) ?? null
    );
  }

  #getFieldsetOptionIndex(fieldset) {
    const optionIndex = Number(fieldset.dataset.optionIndex);

    return Number.isNaN(optionIndex) ? null : optionIndex;
  }

  #syncSubmitButtonForVariant(variant) {
    const form = document.getElementById(`product-form-${this.sectionId}`);
    if (!form) return;

    const submit = form.querySelector('.product-form__submit');
    if (!submit) return;

    const span = submit.querySelector('span');
    const addLabel = this.dataset.addToCartLabel;
    const soldOutLabel = this.dataset.soldOutLabel;
    const preOrderText = this.#getPreOrderTextForVariant(variant);
    const isEffectivelyOutOfStock = this.#isEffectivelyOutOfStock(variant);

    this.#syncPreOrderHint(variant);

    if (preOrderText && isEffectivelyOutOfStock) {
      submit.disabled = false;

      if (span) {
        span.textContent = this.dataset.preOrderLabel || 'Pre Order';

        const note = document.createElement('span');
        note.className = 'product-form__pre-order-note';
        note.textContent = preOrderText;
        span.append(note);
      }

      this.#setPreOrderProperty(form, preOrderText);

      const productRoot = this.closest('.product');
      const pers = productRoot?.querySelector('product-personalisation');
      if (pers && typeof pers.syncAddToCartWithPersonalization === 'function') {
        pers.syncAddToCartWithPersonalization();
      }

      const photoStitch = productRoot?.querySelector('product-photo-stitch');
      if (photoStitch && typeof photoStitch.syncAddToCartWithPhotoStitch === 'function') {
        photoStitch.syncAddToCartWithPhotoStitch();
      }

      this.#syncProductContextTriggers(submit);

      return;
    }

    this.#clearPreOrderProperty(form);

    if (!isEffectivelyOutOfStock) {
      submit.disabled = false;

      if (span && addLabel) {
        span.textContent = addLabel;
      }
    } else {
      submit.disabled = true;

      if (span && soldOutLabel) {
        span.textContent = soldOutLabel;
      }
    }

    const productRoot = this.closest('.product');
    const pers = productRoot?.querySelector('product-personalisation');
    if (pers && typeof pers.syncAddToCartWithPersonalization === 'function') {
      pers.syncAddToCartWithPersonalization();
    }

    const photoStitch = productRoot?.querySelector('product-photo-stitch');
    if (photoStitch && typeof photoStitch.syncAddToCartWithPhotoStitch === 'function') {
      photoStitch.syncAddToCartWithPhotoStitch();
    }

    this.#syncProductContextTriggers(submit);
  }

  initVariantData() {
    const script = this.parentElement?.querySelector('script[type="application/json"][data-product-variants]');

    if (!script) {
      return;
    }

    try {
      const data = JSON.parse(script.textContent);

      if (Array.isArray(data)) {
        this.variantData = data;
      }
    } catch (e) {
      console.error('Failed to parse variant data', e);
    }
  }

  setupLinkListeners() {
    // Handle "Combined Listing" Links - These trigger a full section toggle
    for (const link of this.querySelectorAll('.swatch-link')) {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const url = link.getAttribute('href');
        if (url) {
          this.handleCombinedListingNavigation(url);
        }
      });
    }
  }

  setupLabelUpdates() {
    const fieldsets = this.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      const labelValue = fieldset.querySelector('.form__label-value');
      if (!labelValue) continue;

      const defaultText = labelValue.textContent;

      // Handler for Inputs (Standard)
      const inputs = fieldset.querySelectorAll('input');
      for (const input of inputs) {
        input.addEventListener('change', (e) => {
          labelValue.textContent = e.target.value;
        });

        // Hover Label
        const label = input.nextElementSibling;
        if (label && label.tagName === 'LABEL') {
          label.addEventListener('mouseover', () => {
            labelValue.textContent = input.value;
          });
          label.addEventListener('mouseout', () => {
            const checked = fieldset.querySelector('input:checked');
            labelValue.textContent = checked ? checked.value : defaultText;
          });
        }
      }

      // Handler for Links (Combined)
      const links = fieldset.querySelectorAll('.swatch-link');
      for (const link of links) {
        const value = link.title;
        // Better: get the nested span
        const cleanValue = link.querySelector('.visually-hidden').textContent;

        link.addEventListener('mouseover', () => {
          labelValue.textContent = cleanValue;
        });

        link.addEventListener('mouseout', () => {
          // Check if there's a selected link
          const selected = fieldset.querySelector('.swatch-link.selected');

          if (selected) {
            labelValue.textContent = selected.querySelector('.visually-hidden').textContent;
          } else {
            // If no input checked (mixed mode?) or no link selected
            const checkedInput = fieldset.querySelector('input:checked');
            if (checkedInput) {
              labelValue.textContent = checkedInput.value;
            } else {
              labelValue.textContent = defaultText;
            }
          }
        });
      }
    }
  }

  /**
   * HANDLER: Standard Variant Selection (Inputs)
   * Updates URL, Price, Buttons - Does NOT reload page or replace grid.
   */
  onVariantChange(e) {
    this.updateOptions(); // Filter available options based on selection
    this.checkSelectionStatus(); // Reveal hidden fieldsets

    if (e.target.tagName !== 'INPUT') return;

    const selectedOptions = this.#getSelectedOptionsFromInputs();
    const allSelected = selectedOptions.every((v) => v !== null);

    // Gallery may omit some variant media (e.g. combined-listing parent shows parent + child featured only).
    // scrollToMediaId returns false when that slide is not in the DOM — avoid relying on a silent no-op.
    this.#syncGalleryForCurrentSelection();

    if (!allSelected) {
      if (this.dataset.requiresExplicitVariant === 'true') {
        this.#applyIncompleteVariantUI();
      } else {
        this.#syncKlaviyoBisTrigger(null);
        this.#syncPreOrderHint(null);
      }
      return;
    }

    if (this.variantData) {
      const currentVariant = this.variantData.find((variant) => {
        return variant.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (currentVariant) {
        this.updateURL(currentVariant);
        this.updateFormID(currentVariant);

        this.#syncSubmitButtonForVariant(currentVariant);

        this.updateSectionParts(currentVariant);

        this.#syncKlaviyoBisTrigger(currentVariant);
      } else {
        this.#syncKlaviyoBisTrigger(null);
        this.#syncPreOrderHint(null);
      }
    } else {
      this.#syncKlaviyoBisTrigger(null);
      this.#syncPreOrderHint(null);
    }
  }

  checkSelectionStatus() {
    // If we have a selection (e.g. Color), reveal the other options
    const hiddenFields = this.querySelectorAll('.visually-hidden-initially');
    for (const field of hiddenFields) {
      field.style.display = 'block';
    }
  }

  updateOptions() {
    if (!this.variantData) return;

    const fieldsets = Array.from(this.querySelectorAll('fieldset.product-form__input'));

    for (const fieldset of fieldsets) {
      const index = this.#getFieldsetOptionIndex(fieldset);

      if (index === null) continue;

      const inputs = fieldset.querySelectorAll('input');

      for (const input of inputs) {
        const value = input.value;
        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        const optionWrapper = input.closest('.variant-option-wrapper');

        // Check if this value is compatible with other CURRENT selections
        const isCompatible = this.variantData.some((variant) => {
          // Maximum Match Strategy:
          // Does this variant have [Option_i == value]?
          if (variant.options[index] !== value) return false;

          // AND does it match all OTHER currently selected options?
          return fieldsets.every((otherFieldset) => {
            const otherIndex = this.#getFieldsetOptionIndex(otherFieldset);

            if (otherIndex === null || index === otherIndex) return true;

            const checked = otherFieldset.querySelector('input:checked');
            const selectedValue = checked ? checked.value : null;

            // If other option has a selection, we MUST match it
            if (selectedValue) {
              return variant.options[otherIndex] === selectedValue;
            }
            // If other option has NO selection, any value there is fine (we are just checking existence)
            return true;
          });
        });

        if (isCompatible) {
          input.disabled = false;
          if (label) label.style.display = '';
          if (optionWrapper) optionWrapper.style.display = '';
        } else {
          input.disabled = true;
          if (label) label.style.display = 'none';
          if (optionWrapper) optionWrapper.style.display = 'none';

          // If the hidden option was checked, uncheck it to prevent invalid state
          if (input.checked) {
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }

    this.#syncOutOfStockStyles();
  }

  /**
   * Grey pill styling for option values whose resolved variant is sold out (still selectable).
   */
  #syncOutOfStockStyles() {
    if (!this.variantData) return;

    const fieldsets = Array.from(this.querySelectorAll('fieldset.product-form__input'));

    for (const fieldset of fieldsets) {
      const optionIndex = this.#getFieldsetOptionIndex(fieldset);

      if (optionIndex === null) continue;

      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        const label = fieldset.querySelector(`label[for="${input.id}"]`);

        if (!label || !label.classList.contains('variant-option-label')) {
          continue;
        }

        if (input.disabled) {
          label.classList.remove('variant-option-label--out-of-stock');
          continue;
        }

        const variant = this.#findVariantForOptionValue(optionIndex, input.value);
        const isOutOfStock = this.#isEffectivelyOutOfStock(variant) && !this.#isPreOrderVariant(variant);

        label.classList.toggle('variant-option-label--out-of-stock', isOutOfStock);
      }
    }
  }

  updateURL(variant) {
    if (!variant) return;
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ path: url.toString() }, '', url.toString());
  }

  updateFormID(variant) {
    const form = document.getElementById(`product-form-${this.sectionId}`);
    if (!form) return;
    const input = form.querySelector('input[name="id"]');
    if (input) {
      input.value = variant.id;
      input.disabled = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateSectionParts(variant) {
    // Fetch valid HTML for Price and Buttons
    const url = `${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`;

    const sectionRoot =
      this.closest('.section-product') || this.closest('.shopify-section') || document.documentElement;

    fetchHTML(url)
      .then((html) => {
        // 1. Update Price
        const oldPrice = sectionRoot.querySelector('.product__price');
        const newPrice = html.querySelector('.product__price');
        if (oldPrice && newPrice) oldPrice.innerHTML = newPrice.innerHTML;

        // 2. Update Buy Buttons (Add to Cart / Sold Out)
        const oldButtons = sectionRoot.querySelector('.product-form__buttons');
        const newButtons = html.querySelector('.product-form__buttons');
        if (oldButtons && newButtons) oldButtons.innerHTML = newButtons.innerHTML;

        const formId = `product-form-${this.sectionId}`;
        const oldForm = document.getElementById(formId);
        const newForm = html.getElementById(formId);

        if (oldForm && newForm) {
          const newProp = newForm.querySelector('[name="properties[Pre Order]"]');

          if (newProp?.value) {
            this.#setPreOrderProperty(oldForm, newProp.value);
          } else {
            this.#clearPreOrderProperty(oldForm);
          }
        }

        this.#syncSubmitButtonForVariant(variant);

        for (const el of sectionRoot.querySelectorAll('product-personalisation')) {
          if (typeof el.syncAddToCartWithPersonalization === 'function') {
            el.syncAddToCartWithPersonalization();
          }
        }

        for (const el of sectionRoot.querySelectorAll('product-photo-stitch')) {
          if (typeof el.syncAddToCartWithPhotoStitch === 'function') {
            el.syncAddToCartWithPhotoStitch();
          }
        }

        // 3. Update Inventory (if present)
        const oldInventory = sectionRoot.querySelector('.product__inventory');
        const newInventory = html.querySelector('.product__inventory');
        if (oldInventory && newInventory) oldInventory.innerHTML = newInventory.innerHTML;

        // 4. Update Product Add-on (variant metafield custom.product_add_on)
        const oldAddOn = sectionRoot.querySelector('[data-product-add-on]');
        const newAddOn = html.querySelector('[data-product-add-on]');

        if (oldAddOn && newAddOn) {
          oldAddOn.replaceWith(document.importNode(newAddOn, true));
        } else if (oldAddOn) {
          oldAddOn.remove();
        }
      })
      .catch((e) => console.error(e));
  }

  /**
   * HANDLER: Combined Listing Selection (Links)
   * AJAX for grid, size guide, product description, and Loox reviews (#looxReviews) when present.
   */
  handleCombinedListingNavigation(url) {
    const productGrid = this.closest('.product__grid');
    if (productGrid) {
      productGrid.style.opacity = '0.5';
      productGrid.style.pointerEvents = 'none';
    }

    let fetchUrl;

    try {
      fetchUrl = buildProductSectionRenderUrl(url, this.sectionId);
    } catch {
      window.location.href = url;
      return;
    }

    fetchHTML(fetchUrl)
      .then((html) => {
        refreshSizeGuideFromDocument(html);

        const destination = document.querySelector('.product__grid');
        const source = html.querySelector('.product__grid');

        if (destination && source) {
          destination.innerHTML = source.innerHTML;

          if (typeof window.refreshProductPersonalisation === 'function') {
            window.refreshProductPersonalisation(destination);
          }

          if (typeof window.refreshProductPhotoStitch === 'function') {
            window.refreshProductPhotoStitch(destination);
          }

          if (typeof window.refreshSmartWishlistMounts === 'function') {
            window.refreshSmartWishlistMounts(destination);
          }

          stopLooxReviewsPrimeWatcher();
          clearCombinedListingParentLooxAttrs();

          let historyTarget = url;

          try {
            const historyUrl = new URL(url, window.location.origin);
            const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');

            if (previewThemeId) {
              historyUrl.searchParams.set('preview_theme_id', previewThemeId);
            }

            historyTarget = historyUrl.pathname + historyUrl.search;
          } catch {
            historyTarget = url;
          }

          window.history.pushState({}, '', historyTarget);

          if (window.dockManager) {
            window.dockManager.initStickyObserver();
          }
        }
      })
      .then(() =>
        Promise.all([refreshProductDescriptionSection(url), refreshLooxReviewsSection(url)]),
      )
      .catch((e) => {
        console.error(e);
        window.location.href = url; // Fallback
      })
      .finally(() => {
        if (productGrid) {
          productGrid.style.opacity = '1';
          productGrid.style.pointerEvents = 'auto';
        }
      });
  }
}

customElements.define('variant-picker', VariantPicker);

/**
 * Custom Element: Product Modal (Zoom)
 */
class ProductModal extends HTMLElement {
  constructor() {
    super();
    this.opened = false;
    this.contentInfo = null;
    this.btnClose = null;
  }

  connectedCallback() {
    this.contentInfo = this.querySelector('.product-media-modal__content-info');
    this.btnClose = this.querySelector('.product-media-modal__close');

    if (this.btnClose) {
      this.btnClose.addEventListener('click', this.close.bind(this));
    }
  }

  open(mediaHtml) {
    if (!this.contentInfo) {
      return;
    }

    this.contentInfo.innerHTML = mediaHtml;
    this.setAttribute('open', '');
    this.opened = true;
    document.body.style.overflow = 'hidden';
    this.querySelector('.product-media-modal__content')?.focus();
  }

  close() {
    this.removeAttribute('open');
    if (this.contentInfo) {
      this.contentInfo.innerHTML = '';
    }
    this.opened = false;
    document.body.style.overflow = '';
  }
}
customElements.define('product-modal', ProductModal);

/**
 * Handle Zoom Button Clicks (delegated — gallery HTML is replaced on combined-listing swatch navigation)
 */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('.product-media__zoom-btn');
  if (!btn) return;

  const sectionRoot = btn.closest('[id^="shopify-section-"]');
  const liquidSectionId = sectionRoot?.id?.replace(/^shopify-section-/, '') ?? '';
  const modal =
    (liquidSectionId && document.getElementById(`ProductModal-${liquidSectionId}`)) ||
    document.querySelector('product-modal');
  const mediaContainer = btn.closest('.product-media');
  const image = mediaContainer?.querySelector('img');

  if (modal && image) {
    // Clone the image for the modal, removing specific responsive sizing to let it be full size
    const clone = image.cloneNode(true);
    clone.removeAttribute('class');
    clone.removeAttribute('sizes');
    clone.style.width = 'auto';
    clone.style.height = 'auto';
    clone.style.maxHeight = '90vh';
    clone.style.maxWidth = '90vw';
    modal.open(clone.outerHTML);
  }
});

/**
 * Custom Element: Gallery Slider
 * Handles horizontal scroll buttons, dots, and variant syncing.
 */
class GallerySlider extends HTMLElement {
  #videoJumpClickHandler = (e) => {
    e.preventDefault();

    const id = e.currentTarget.dataset.videoMediaId;
    if (!id || !this.list) return;

    this.scrollToMediaId(id, 'auto');
  };

  constructor() {
    super();

    this.onScrollHandler = this.onScroll.bind(this);
    this.onPrevClick = () => this.scroll('left');
    this.onNextClick = () => this.scroll('right');
  }

  connectedCallback() {
    this.list = this.querySelector('.product__media-gallery');
    this.prevBtn = this.querySelector('[name="previous"]');
    this.nextBtn = this.querySelector('[name="next"]');
    this.dots = this.querySelectorAll('.slider-dot');
    this.videoJumpBtn = this.querySelector('.product-gallery__video-jump');

    if (!this.list) return;

    if (this.videoJumpBtn) {
      this.videoJumpBtn.addEventListener('click', this.#videoJumpClickHandler);
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrevClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNextClick);

    if (this.dots) {
      let dotIndex = 0;
      for (const dot of this.dots) {
        dot.addEventListener('click', () => this.scrollToIndex(dotIndex));
        dotIndex++;
      }
    }

    // Throttled Scroll Listener
    let ticking = false;
    this.list.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.onScrollHandler();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Initial check
    this.onScrollHandler();
  }

  disconnectedCallback() {
    if (this.videoJumpBtn) {
      this.videoJumpBtn.removeEventListener('click', this.#videoJumpClickHandler);
      this.videoJumpBtn = null;
    }

    if (!this.list) return;

    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNextClick);
  }

  scroll(direction) {
    const scrollAmount = this.list.clientWidth;
    this.list.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }

  scrollToIndex(index) {
    const item = this.list.children[index];
    if (item) {
      this.list.scrollTo({
        left: item.offsetLeft,
        behavior: 'smooth',
      });
    }
  }

  onScroll() {
    // Update button states
    if (this.prevBtn) this.prevBtn.disabled = this.list.scrollLeft <= 5;
    if (this.nextBtn) this.nextBtn.disabled = this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 5;

    if (!this.dots?.length) {
      return;
    }

    const items = Array.from(this.list.children).filter((el) => el.nodeType === Node.ELEMENT_NODE);

    if (items.length === 0) {
      return;
    }

    const scrollLeft = this.list.scrollLeft;
    let index = 0;
    let bestDist = Infinity;

    for (let i = 0; i < items.length; i++) {
      const dist = Math.abs(items[i].offsetLeft - scrollLeft);

      if (dist < bestDist) {
        bestDist = dist;
        index = i;
      }
    }

    let i = 0;
    for (const dot of this.dots) {
      const isActive = i === index;
      dot.setAttribute('aria-current', isActive);
      dot.classList.toggle('active', isActive);
      i++;
    }
  }

  /**
   * @param {string|number} mediaId
   * @param {'auto' | 'smooth'} [scrollBehavior='auto'] Variant sync uses `auto`; video jump uses `auto` so the gallery does not animate through every slide.
   * @returns {boolean} True when a matching `[data-media-id]` slide exists (false if gallery HTML filtered it out).
   */
  scrollToMediaId(mediaId, scrollBehavior = 'auto') {
    const item = this.list.querySelector(`[data-media-id="${mediaId}"]`);
    if (item) {
      const behavior = scrollBehavior === 'smooth' ? 'smooth' : 'auto';

      this.list.scrollTo({
        left: item.offsetLeft,
        behavior,
      });
      return true;
    }
    return false;
  }
}

customElements.define('gallery-slider', GallerySlider);
